/**
 * Provides an Apollo Link for forwarding/routing operations to specific batches based on their names.
 *
 * The majority of queries should be executed in the default batch to reduce the number of concurrent
 * backend requests and to maximize the data shared between query loads. While concurrent requests are usually
 * ideal for the end user, they result in spikes and contention for web workers leading to deploy degredation.
 * There is also a non-trivial startup cost on each hit of the graphql endpoint.
 *
 * Putting some queries in separate batches improves our ability to perform partial loads of the website by
 * returning related sets of data sooner, and without waiting on other queries from other components.
 *
 * Situations to consider for separate batches:
 *  - A query is known to be slow and often runs alongside other faster queries (page start or on user action)
 *  - Multiple queries are restricted to a specific set of components (domain)
 *
 * Note that Apollo Client dedupes based on the query, so two instances of the same query should never be in
 * flight at once.
 *
 * An alternative to this would be to use batchKey, and return a different batch key for operations that
 * shouldn't be batched. The problem with that is that the link still waits batchInterval for that bucket to
 * fill up, which then means other requests are fired before it. These queries were chosen _not_ to be
 * batched because they're slow, and firing slow requests at the end ultimately makes your page load much
 * longer.
 */

import {ApolloLink, Observable, Operation, RequestHandler} from '@apollo/client';

import {GET_ANALYSIS_QUERY} from 'analysis-tool/graphql/GetAnalysis';
import {JELLYFISH_VIEW_QUERY} from 'app-core/archetype/workspace/JellyfishView';
import {BENCHLING_APP_FILE_STATE_QUERY} from 'benchling-apps/backbone/BenchlingAppFileState';
import {BATCHED_CANVAS_QUERY} from 'benchling-apps/interactivity/canvases/CanvasContainer';
import BenchlingConfig from 'BenchlingConfig';
import {MOLECULE_FILE_QUERY} from 'chemistry/molecules/tabs/MoleculeFileState';
import {MIXTURE_FILE_QUERY} from 'folder-items/Mixture';
import {FORM_DEFINITION_FILE_STATE_QUERY} from 'forms/form-definition/FormDefinitionQueries';
import {createSingleBatchLink} from 'graphql-client/createBatchLink';
import {GET_ANALYTICS_DASHBOARD_QUERY} from 'insights/analytics-dashboards/details/graphql/GetAnalyticsDashboard';
import {GET_BIOENTITY_ANALYTICS_DASHBOARD_QUERY} from 'insights/embeddings/data/AnalyticsDashboardLinkActions';
import {CONNECTION_QUERY} from 'instruments/connections/connection-editor/ConnectionDetails';
import {MCP_TOOL_MAPPING_QUERY} from 'llm/mcp-client-library/MCPToolMappingQuery';
import {BULK_ASSEMBLY_FILE_QUERY} from 'molbio/bulk-assembly/backbone/BulkAssemblyFileState';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {USER_MENU_QUERY} from 'nav/navbar/UserMenu';
import {ALL_PROCESS_TASK_SCHEMAS_QUERY, ITEM_LISTING_SCHEMAS_QUERY} from 'nav/sidepanel/listing/ItemListing';
import {ALL_ASSAY_SCHEMAS_QUERY, ALL_INVENTORY_SCHEMAS_QUERY} from 'nav/sidepanel/SidepanelQueries';
import {NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {PROCESS_FILE_QUERY} from 'processes/process-details/ProcessFileState';
import {PROCESS_TASK_FILE_STATE_QUERY} from 'processes/process-task-details/graphql/GetProcessTask';
import {PROCESS_TASK_SCHEMA_EDITOR_QUERY} from 'processes/settings/ProcessTaskSchemaEditor';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {ASSAY_SCHEMAS_PAGE_WITHOUT_FIELDS_QUERY} from 'results/assay-schemas/AssaySchemasPage';
import {TAG_SCHEMAS_WITH_REGISTRY_QUERY} from 'schemas/util/useTagSchemasQuery';
import {CONNECTED_SEARCH_QUERY} from 'search/withSearching';
import {REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_QUERY} from 'settings/registry/RegistryTagSchemasSettingsPage';
import {REGISTRY_SETTINGS_QUERY} from 'sidebar/RegistrySettings';
import {STUDY_FILE_QUERY} from 'studies/workspace/StudyFileState';
import {BULK_CHIP_ITEM_PREFETCH_QUERY} from 'ui-library/chips/useSmartChipQuery';

import {getQueryDefinition} from './AST';

const customBatchMapping = [
  {
    // We separate out all initial file load queries, since these meet the two following criteria:
    // 1. They are always user blocking (i.e. on page load, or opening a file)
    // 2. They are often run at the same time as many other expensive GraphQL queries (on page load,
    //    we have a bunch of expensive queries for schema data + sidepanel population.)
    // This would ideally not be necessary if the other queries were fast, so this is viewed
    // as a mild hack to significantly improve page load time.
    matches: [
      BENCHLING_APP_FILE_STATE_QUERY,
      BULK_ASSEMBLY_FILE_QUERY,
      CONNECTION_QUERY,
      FORM_DEFINITION_FILE_STATE_QUERY,
      GET_ANALYSIS_QUERY,
      GET_ANALYTICS_DASHBOARD_QUERY,
      GET_BIOENTITY_ANALYTICS_DASHBOARD_QUERY, // This is run as part of opening registry items
      JELLYFISH_VIEW_QUERY,
      MIXTURE_FILE_QUERY,
      MOLECULE_FILE_QUERY,
      PROCESS_FILE_QUERY,
      PROCESS_TASK_FILE_STATE_QUERY,
      STUDY_FILE_QUERY,
    ],
    batchMax: 50,
    batchInterval: 10,
  },
  // We batch BATCHED_CANVAS_QUERY because we poll for it, and we want to batch those poll requests together
  // to reduce the total number of network requests (there might be many components on a single page
  // that are executing the CANVAS_QUERY polling loop).
  {
    matches: [BATCHED_CANVAS_QUERY],
    batchMax: 50,
    batchInterval: BenchlingConfig.BENCHLING_APP_CANVAS_POLLING_FREQUENCY_MS,
  },
  {
    matches: [ALL_SOURCES_QUERY, ALL_ASSAY_SCHEMAS_QUERY],
    batchMax: 1,
    batchInterval: 10,
  },
  // These isolated/unrelated menu and settings page queries are designed to be minimal/fast and should not be batched
  // so the corresponding components are rendered quickly.
  {
    matches: [
      USER_MENU_QUERY,
      REGISTRY_SETTINGS_QUERY,
      REGISTRY_TAG_SCHEMAS_SETTINGS_PAGE_QUERY,
      ASSAY_SCHEMAS_PAGE_WITHOUT_FIELDS_QUERY,
    ],
    batchMax: 1,
    batchInterval: 10,
  },
  // Sidepanel fetches all the registry schemas except TagSchemas; batching them in the same network
  // request means we can cache Registry.get_with_permission, which tends to dominate those queries
  // perf-wise.
  {
    matches: [ALL_INVENTORY_SCHEMAS_QUERY, ALL_TAG_SCHEMAS_QUERY, TAG_SCHEMAS_WITH_REGISTRY_QUERY],
    batchMax: 2,
    batchInterval: 10,
  },
  // ItemListing and withSearching run quite expensive queries to fetch schemas; creating a separate
  // network request to avoid batching it with other queries and slowing down load times of other
  // components by combining it with this very expensive query. Running them separately to parallelize.
  {matches: [CONNECTED_SEARCH_QUERY], batchMax: 1, batchInterval: 10},
  {matches: [ITEM_LISTING_SCHEMAS_QUERY], batchMax: 1, batchInterval: 10},
  // Notebook Entries employ some custom batching for chips in Entries. These have already been batched
  // (so we don't need to batch more than one together) and they run slowly, so we do not want to batch
  // them with other queries, either.
  {matches: [BULK_CHIP_ITEM_PREFETCH_QUERY], batchMax: 1, batchInterval: 10},
  // Notebook Lookup Config Modal Query loads large numbers of schmeas on some tenants.
  {matches: [NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY], batchMax: 1, batchInterval: 10},
  // MCP Tool Mapping Query requires a separate network request to the MCP server and is slow.
  {matches: [MCP_TOOL_MAPPING_QUERY], batchMax: 1, batchInterval: 10},
  // Unbatching for potential performance gain as this query significantly slows other queries.
  {
    matches: [ALL_PROCESS_TASK_SCHEMAS_QUERY, ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY],
    batchMax: 1,
    batchInterval: 10,
  },
  // Unbatching to help task schema page load faster.
  {matches: [PROCESS_TASK_SCHEMA_EDITOR_QUERY], batchMax: 10, batchInterval: 10},
].map((entry) => ({
  matches: entry.matches.map((query) => getQueryDefinition(query).name?.value),
  batchMax: entry.batchMax,
  batchInterval: entry.batchInterval,
}));

/**
 * Forward query to specific batch link based on Query name
 */
export default function createBatchLinkForwarder(): ApolloLink | RequestHandler {
  // New default batching strategy!
  if (BenchlingConfig.PERF_GRAPH_QL_BATCH_STRATEGY > Math.floor(Math.random() * 100)) {
    // This makes the batching link O(1) lookup, creating a batch link for any operation name that doesn't
    // have one, then forwarding the request to the batch.
    const batchLinkMap = new Map<string, ApolloLink>();
    for (const batch of customBatchMapping) {
      const batchLink = createSingleBatchLink({batchMax: batch.batchMax, batchInterval: batch.batchInterval});
      for (const opName of batch.matches) {
        if (opName != null) {
          batchLinkMap.set(opName, batchLink);
        }
      }
    }
    return new ApolloLink((operation: Operation) => {
      if (!batchLinkMap.has(operation.operationName)) {
        batchLinkMap.set(operation.operationName, createSingleBatchLink({batchMax: 200, batchInterval: 10}));
      }
      return batchLinkMap.get(operation.operationName)?.request(operation) || Observable.of();
    });
  }

  // This unrolls as a list of batches that are connected by ApolloLink.split, ending with the default batch
  // if the operation is not matched on any of the above functions
  //                       ApolloLink.split(op)
  //                          /              \
  //                    BatchLink        ApolloLink.split(op)
  //                                          /          \
  //                                  BatchLink        BatchLink(defaults)
  const defaultBatch: ApolloLink = createSingleBatchLink({batchMax: 200, batchInterval: 10});
  return customBatchMapping.reduce(
    (prevLink, customBatch) =>
      ApolloLink.split(
        (op) => customBatch.matches.some((operationName) => operationName === op.operationName),
        createSingleBatchLink({
          batchMax: customBatch.batchMax,
          batchInterval: customBatch.batchInterval,
        }),
        prevLink
      ),
    defaultBatch
  );
}
